/**
 * Where the moulds are, and whose they are.
 *
 * Every figure is counted by the server (GET /analytics/overview), never from
 * this device's copy of the catalog: the point of the screen is a number the
 * business can quote, and a device counts whatever it last synced.
 *
 * A mould's location is where it was last scanned - the same rule as the rest
 * of the product - so a mould nobody has scanned yet is "not placed", not
 * guessed. Every supplier is its own location of type Vendor; the share chart
 * adds them together as "Supplier locations", the bar chart lists each one.
 *
 * Clicking a location or a customer narrows everything else to it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PieChart, RefreshCw, X } from 'lucide-react';
import type { AnalyticsOverview, AnalyticsQuery } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Input, Select } from '../../components/ui/field.js';
import { Card, CardBody, CardHeader, CardTitle, EmptyState } from '../../components/ui/primitives.js';
import { analyticsOverview, isNetworkError } from '../../api/client.js';
import { formatTime } from '../../lib/format.js';
import { BarList, Donut, formatPct, type ChartItem } from './charts.js';

const SUPPLIERS = 'suppliers';
const NONE = 'none';
const SLOTS = 8;
const series = (i: number) => (i < SLOTS ? `var(--series-${i + 1})` : 'var(--series-other)');
const NEUTRAL = 'var(--series-other)';
/** Customers shown by name before the rest fold into "Other customers". */
const TOP_CUSTOMERS = 10;

export function AnalysisPage() {
  const [query, setQuery] = useState<AnalyticsQuery>({});
  const [search, setSearch] = useState('');
  const [data, setData] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Typing narrows by code or name, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery((q) => (q.q === (search.trim() || undefined) ? q : { ...q, q: search.trim() || undefined })), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (q: AnalyticsQuery) => {
    setLoading(true);
    setError(null);
    try {
      setData(await analyticsOverview(q));
    } catch (e) {
      setError(isNetworkError(e) ? 'No connection. These figures are counted on the server.' : 'Could not load the figures.');
    } finally {
      setLoading(false);
    }
  }, []);

  // A range that ends before it starts is a slip, not a question with zero answers.
  const badRange = !!(query.from && query.to && query.from > query.to);

  useEffect(() => {
    if (badRange) return;
    void load(query);
  }, [load, query, badRange]);

  const set = (patch: Partial<AnalyticsQuery>) => setQuery((q) => ({ ...q, ...patch }));
  const filtered = !!(query.locationId || query.customerId || query.from || query.to || query.q);

  // Colour per entity, from a fixed order of every site the business has -
  // not from this view's ranking - so a filter never repaints a location.
  const siteColor = useMemo(() => {
    const sites = (data?.options.locations ?? []).filter((l) => l.type !== 'VENDOR').map((l) => l.id);
    const map = new Map<string, string>();
    sites.forEach((id, i) => map.set(id, series(i)));
    map.set(SUPPLIERS, series(sites.length));
    return map;
  }, [data?.options.locations]);

  const vendorIds = useMemo(
    () => new Set((data?.options.locations ?? []).filter((l) => l.type === 'VENDOR').map((l) => l.id)),
    [data?.options.locations],
  );

  /** The share chart: each site, all suppliers together, and the unplaced. */
  const shareItems = useMemo<ChartItem[]>(() => {
    if (!data) return [];
    const items: ChartItem[] = [];
    let suppliers = 0;
    for (const s of data.byLocation) {
      if (s.id === null) continue;
      if (vendorIds.has(s.id)) suppliers += s.count;
      else items.push({ key: s.id, label: s.name, value: s.count, color: siteColor.get(s.id) ?? NEUTRAL });
    }
    const order = [...siteColor.keys()];
    items.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    if (vendorIds.size) items.push({ key: SUPPLIERS, label: 'Supplier locations', value: suppliers, color: siteColor.get(SUPPLIERS)! });
    const unplaced = data.byLocation.find((s) => s.id === null);
    if (unplaced?.count) items.push({ key: NONE, label: 'Not placed yet', value: unplaced.count, color: NEUTRAL });
    return items;
  }, [data, siteColor, vendorIds]);

  /** The count chart: every location on its own, one colour - the length is the message. */
  const countItems = useMemo<ChartItem[]>(
    () =>
      (data?.byLocation ?? []).map((s) => ({
        key: s.id ?? NONE,
        label: s.name,
        value: s.count,
        color: s.id === null ? NEUTRAL : 'var(--series-1)',
        tag: s.id && vendorIds.has(s.id) ? 'supplier' : undefined,
      })),
    [data?.byLocation, vendorIds],
  );

  const customerItems = useMemo<ChartItem[]>(() => {
    if (!data) return [];
    const named = data.byCustomer.filter((c) => c.id !== null);
    const top = named.slice(0, TOP_CUSTOMERS);
    const rest = named.slice(TOP_CUSTOMERS);
    const items: ChartItem[] = top.map((c) => ({ key: c.id!, label: c.name, value: c.count, color: 'var(--series-1)' }));
    if (rest.length) {
      items.push({
        key: 'others',
        label: `Other customers (${rest.length})`,
        value: rest.reduce((a, b) => a + b.count, 0),
        color: 'var(--series-1)',
      });
    }
    const none = data.byCustomer.find((c) => c.id === null);
    if (none) items.push({ key: NONE, label: 'No customer recorded', value: none.count, color: NEUTRAL });
    return items;
  }, [data]);

  const selectLocation = (key: string) => set({ locationId: key === query.locationId ? undefined : key });
  const selectCustomer = (key: string) => {
    if (key === 'others') return;
    set({ customerId: key === query.customerId ? undefined : key });
  };

  const locationName =
    query.locationId === SUPPLIERS
      ? 'supplier locations'
      : query.locationId === NONE
        ? 'moulds not placed yet'
        : data?.options.locations.find((l) => l.id === query.locationId)?.name;

  return (
    <Page
      title="Analysis"
      description={
        data
          ? `Counted on the server at ${formatTime(data.generatedAt)}${locationName ? ` · ${locationName}` : ''}`
          : 'How many moulds, where, and for which customers.'
      }
      actions={
        <Button variant="secondary" onClick={() => void load(query)} loading={loading}>
          <RefreshCw className="size-4" /> Refresh
        </Button>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-2">
          <Select
            aria-label="Location"
            value={query.locationId ?? ''}
            onChange={(e) => set({ locationId: e.target.value || undefined })}
            className="w-48"
            options={[
              { value: '', label: 'All locations' },
              ...(vendorIds.size ? [{ value: SUPPLIERS, label: 'All supplier locations' }] : []),
              ...(data?.options.locations ?? []).map((l) => ({
                value: l.id,
                label: l.name,
                hint: l.type === 'VENDOR' ? 'supplier' : undefined,
              })),
              { value: NONE, label: 'Not placed yet' },
            ]}
          />
          <Select
            aria-label="Customer"
            value={query.customerId ?? ''}
            onChange={(e) => set({ customerId: e.target.value || undefined })}
            className="w-48"
            options={[
              { value: '', label: 'All customers' },
              ...(data?.options.customers ?? []).map((c) => ({ value: c.id, label: c.name })),
              { value: NONE, label: 'No customer recorded' },
            ]}
          />
          <label className="flex flex-col text-xs text-muted-foreground">
            Added from
            <Input type="date" value={query.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} className="w-40" />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">
            to
            <Input type="date" value={query.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} className="w-40" />
          </label>
          <Input
            aria-label="Mould code or name"
            placeholder="Mould code or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-52"
          />
          {filtered ? (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch('');
                setQuery({});
              }}
            >
              <X className="size-4" /> Clear filters
            </Button>
          ) : null}
        </div>
      }
    >
      {badRange ? (
        <p role="alert" className="mb-3 rounded-lg bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
          The “Added from” date is after the “to” date. Swap them to see moulds added between the two.
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
          <Button variant="ghost" size="sm" onClick={() => void load(query)}>
            Try again
          </Button>
        </p>
      ) : null}

      {!data ? (
        <EmptyState icon={<PieChart />} title={loading ? 'Counting…' : 'No figures yet'} description="The figures are counted on the server." />
      ) : (
        <div className={loading ? 'flex flex-col gap-4 opacity-60 transition-opacity' : 'flex flex-col gap-4'}>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Stat label="Total moulds" value={data.totals.moulds} />
            <Stat
              label="Customers"
              value={data.totals.customers}
              note={data.totals.noCustomer ? `${data.totals.noCustomer} mould${data.totals.noCustomer === 1 ? '' : 's'} with none recorded` : undefined}
            />
            <Stat
              label="Locations in use"
              value={data.totals.locations}
              note={data.totals.unplaced ? `${data.totals.unplaced} not placed yet` : undefined}
            />
            <Stat label="Photos" value={data.totals.photos} />
          </div>

          {data.totals.moulds === 0 ? (
            <Card>
              <EmptyState icon={<PieChart />} title="No moulds match" description={filtered ? 'Try clearing a filter.' : 'Import or add moulds and they are counted here.'} />
            </Card>
          ) : (
            <>
              <div className="grid gap-4 xl:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Moulds by location</CardTitle>
                    <span className="text-xs text-muted-foreground">share of {data.totals.moulds}</span>
                  </CardHeader>
                  <CardBody>
                    <Donut items={shareItems} centerLabel="moulds" onSelect={selectLocation} />
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Mould count by location</CardTitle>
                    <span className="text-xs text-muted-foreground">every site and supplier</span>
                  </CardHeader>
                  <CardBody>
                    <BarList items={countItems} total={data.totals.moulds} onSelect={selectLocation} />
                  </CardBody>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Customer distribution{locationName ? ` · ${locationName}` : ''}</CardTitle>
                  <span className="text-xs text-muted-foreground">
                    {data.totals.customers} customer{data.totals.customers === 1 ? '' : 's'}
                  </span>
                </CardHeader>
                <CardBody>
                  <BarList items={customerItems} total={data.totals.moulds} showShare onSelect={selectCustomer} />
                  {data.totals.noCustomer === data.totals.moulds ? (
                    <p className="mt-3 text-sm text-muted-foreground">
                      None of these moulds has a customer recorded yet. Importing a sheet with a “Customer” column fills it
                      in - existing values are never overwritten.
                    </p>
                  ) : null}
                </CardBody>
              </Card>

              <p className="text-xs text-muted-foreground">
                A mould&apos;s location is where it was last scanned; an imported mould is placed where the import says.
                {data.totals.unplaced
                  ? ` ${data.totals.unplaced} mould${data.totals.unplaced === 1 ? ' has' : 's have'} never been scanned or placed (${formatPct(data.totals.unplaced, data.totals.moulds)}).`
                  : ''}
              </p>
            </>
          )}
        </div>
      )}
    </Page>
  );
}

function Stat({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-3xl font-semibold tracking-tight text-foreground">{value.toLocaleString('en-IN')}</div>
      {note ? <div className="mt-0.5 text-xs text-muted-foreground">{note}</div> : null}
    </div>
  );
}
